/**
 * Borrowed from Melt UI
 * These brilliant functions were originally created by @ThomasGLopes
 */
import { onDestroy } from "svelte";
import { derived } from "svelte/store";
/**
 * A utility function that creates a derived store that automatically
 * unsubscribes from its dependencies.
 *
 * @template S - The type of the stores object
 * @template T - The type of the derived store
 * @param stores - The stores object to derive from
 * @param fn - The function to derive the store from
 * @returns A derived store that automatically unsubscribes from its dependencies
 */
export function derivedWithUnsubscribe(stores, fn) {
    let unsubscribers = [];
    const onUnsubscribe = (cb) => {
        unsubscribers.push(cb);
    };
    const unsubscribe = () => {
        // console.log('dfu unsubscribing');
        // Call all of the unsubscribe functions from the previous run of the function
        unsubscribers.forEach((fn) => fn());
        // Clear the list of unsubscribe functions
        unsubscribers = [];
    };
    const derivedStore = derived(stores, ($storeValues) => {
        unsubscribe();
        return fn($storeValues, onUnsubscribe);
    });
    onDestroy(unsubscribe);
    const subscribe = (...args) => {
        const unsub = derivedStore.subscribe(...args);
        return () => {
            unsub();
            unsubscribe();
        };
    };
    return {
        ...derivedStore,
        subscribe
    };
}
/**
 * A utility function that creates an effect from a set of stores and a function.
 * The effect is automatically cleaned up when the component is destroyed.
 *
 * @template S - The type of the stores object
 * @param stores - The stores object to derive from
 * @param fn - The function to run when the stores change
 * @returns A function that can be used to unsubscribe the effect
 */
export function effect(stores, fn) {
    // Create a derived store that contains the stores object and an onUnsubscribe function
    const unsub = derivedWithUnsubscribe(stores, (stores, onUnsubscribe) => {
        return {
            stores,
            onUnsubscribe
        };
    }).subscribe(({ stores, onUnsubscribe }) => {
        const returned = fn(stores);
        // If the function returns a cleanup function, call it when the effect is unsubscribed
        if (returned) {
            onUnsubscribe(returned);
        }
    });
    // Automatically unsubscribe the effect when the component is destroyed
    onDestroy(unsub);
    return unsub;
}
